import {
    centsToFormattedDollars,
    formatWithoutTimezone,
    isoDateToUTCTimezoneDate,
    transformEnumToDisplay,
} from '@fruitful/fruitils';
import { AnalyticsUserEvent } from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router } from 'expo-router';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useMemo, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FootnoteRegular,
    FullScreenSpinner,
    ScreenContainer,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { getLastFourAccountNumber } from '../../../common/utils/bankingUtils';
import {
    captureException,
    captureWarning,
    track,
} from '../../../logger/Logger';
import { useGetApexAccounts } from '../../hooks/useGetApexAccounts';
import useGetICTTradingDay from '../../hooks/useGetICTTradingDay';
import useTestICTDeposit, {
    type TestICTDepositRequest,
} from '../../hooks/useTestICTDeposit';
import { apexDepositRequestAtom } from '../../state/investmentAtoms';
import {
    BackdoorRothIctDepositWarningNotice,
    shouldWarnBackdoorRothIctDeposit,
} from './BackdoorRothIctDepositWarningNotice';
import {
    BackdoorTradIctDepositConversionNotice,
    shouldShowBackdoorTradConversionNotice,
} from './BackdoorTradIctDepositConversionNotice';

const DepositItem = (item: { description: string; value: string }) => {
    return (
        <XStack
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            padding={16}
        >
            <BodyMediumRegular
                color="$gray1000"
                flex={1}
                flexShrink={1}
                minWidth={0}
            >
                {item.description}
            </BodyMediumRegular>
            <BodyMediumEmphasized textAlign="right" flex={2} flexShrink={0}>
                {item.value}
            </BodyMediumEmphasized>
        </XStack>
    );
};

export const DepositReview = () => {
    const toast = useToastController();
    const queryClient = useQueryClient();

    const apexDepositRequest = useAtomValue(apexDepositRequestAtom);

    const { mutateAsync: submitIctDepositRequest, isPending } =
        useTestICTDeposit({});

    const { data: tradingDayData, isLoading: isTradingDayLoading } =
        useGetICTTradingDay({});

    const { data: apexAccounts } = useGetApexAccounts();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const formattedTradingDay = useMemo(() => {
        if (tradingDayData?.tradingDay == null) return '';
        const date = isoDateToUTCTimezoneDate(tradingDayData.tradingDay);
        if (isNaN(date.getTime())) return '';
        return formatWithoutTimezone(date, 'MMMM d, yyyy');
    }, [tradingDayData]);

    const isValid = useMemo(() => {
        if (apexDepositRequest === null) {
            return false;
        }

        const {
            amountCents,
            contributionYear,
            fromAccount,
            toAccount,
            contributionType,
            isRetirementAccount,
        } = apexDepositRequest;

        if (toAccount == null) {
            return false;
        }

        if (fromAccount == null) {
            return false;
        }

        if (amountCents == null || amountCents <= 0) {
            return false;
        }

        if (isRetirementAccount && contributionYear == null) {
            return false;
        }

        return !(isRetirementAccount && contributionType == null);
    }, [apexDepositRequest]);

    const ictDepositRequest = useMemo((): TestICTDepositRequest | undefined => {
        if (apexDepositRequest === null) {
            return undefined;
        }

        const {
            amountCents,
            contributionYear,
            fromAccount,
            toAccount,
            contributionType,
            isRetirementAccount,
        } = apexDepositRequest;

        if (fromAccount == null || toAccount == null || amountCents == null) {
            return undefined;
        }

        if (isRetirementAccount) {
            if (contributionType == null || contributionYear == null) {
                return undefined;
            }

            return {
                amountCents,
                fromSyncteraAccountId: fromAccount.id,
                memo: `${toAccount.nickname} deposit ${amountCents} ${toAccount.nickname}`,
                ...(isRetirementAccount
                    ? {
                          retirementContribution: {
                              taxYear: contributionYear,
                              type: contributionType,
                          },
                      }
                    : null),
                toApexAccountId: toAccount.apexAccountId,
            };
        }

        return {
            amountCents,
            fromSyncteraAccountId: fromAccount.id,
            memo: `${toAccount.nickname} deposit ${amountCents} ${toAccount.nickname}`,
            toApexAccountId: toAccount.apexAccountId,
        };
    }, [apexDepositRequest]);

    if (isPending || isTradingDayLoading || isSubmitting) {
        return <FullScreenSpinner />;
    }

    if (apexDepositRequest === null) {
        return <Redirect href="/home/investment" />;
    }

    const {
        amountCents,
        contributionYear,
        fromAccount,
        toAccount,
        contributionType,
        isRetirementAccount,
    } = apexDepositRequest;

    return (
        <ScreenContainer
            onFallbackRoute="/home/investment"
            hasBackButton={true}
            title="Review"
        >
            <YStack flexGrow={1} gap={24} width="100%">
                {shouldWarnBackdoorRothIctDeposit(toAccount) && (
                    <BackdoorRothIctDepositWarningNotice />
                )}
                {shouldShowBackdoorTradConversionNotice(toAccount) && (
                    <BackdoorTradIctDepositConversionNotice
                        traditionalIraAccount={toAccount}
                        apexAccounts={apexAccounts}
                    />
                )}
                <BodyMediumRegular>Review deposit details</BodyMediumRegular>
                <YStack
                    width="100%"
                    backgroundColor="$gray200"
                    borderRadius={16}
                >
                    <XStack
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                        padding={16}
                    >
                        <BodyMediumRegular
                            color="$gray1000"
                            flex={1}
                            flexShrink={1}
                            minWidth={0}
                        >
                            From
                        </BodyMediumRegular>
                        <YStack alignItems="flex-end" flex={2} flexShrink={0}>
                            <BodyMediumEmphasized textAlign="right">
                                {fromAccount?.nickname ??
                                    'Please select a bank account'}
                            </BodyMediumEmphasized>
                            {fromAccount != null && (
                                <FootnoteRegular color="$gray1000">
                                    {fromAccount.bankName} *
                                    {getLastFourAccountNumber(
                                        fromAccount.accountNumberMasked,
                                    )}
                                </FootnoteRegular>
                            )}
                        </YStack>
                    </XStack>
                    <XStack
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                        padding={16}
                    >
                        <BodyMediumRegular
                            color="$gray1000"
                            flex={1}
                            flexShrink={1}
                            minWidth={0}
                        >
                            To
                        </BodyMediumRegular>
                        <YStack alignItems="flex-end" flex={2} flexShrink={0}>
                            <BodyMediumEmphasized textAlign="right">
                                {toAccount?.nickname ??
                                    'Please select an investment account'}
                            </BodyMediumEmphasized>
                            {toAccount != null && (
                                <FootnoteRegular color="$gray1000">
                                    {toAccount.accountNumber}
                                </FootnoteRegular>
                            )}
                        </YStack>
                    </XStack>
                    {isRetirementAccount && (
                        <DepositItem
                            description="Contribution year"
                            value={contributionYear?.toString(10) ?? ''}
                        />
                    )}
                    {isRetirementAccount && (
                        <DepositItem
                            description="Contribution type"
                            value={
                                contributionType != null
                                    ? transformEnumToDisplay(contributionType)
                                    : ''
                            }
                        />
                    )}
                    <DepositItem
                        description="Date"
                        value={formattedTradingDay}
                    />
                    <DepositItem
                        description="Processing time"
                        value="1 - 2 business days"
                    />
                    <DepositItem
                        description="Requested amount"
                        value={centsToFormattedDollars(amountCents ?? 0)}
                    />
                </YStack>
                <FootnoteRegular color="$gray1000">
                    Transfers submitted prior to 4:00pm ET Monday - Friday will
                    be initiated on the same day. Transfers submitted after
                    4:00pm ET or on weekends / holidays will be initiated on the
                    next business day. When the NYSE closes early, transfers
                    need to be submitted by 1:00pm ET to be processed the same
                    day.
                </FootnoteRegular>
                <FootnoteRegular
                    color="$gray1000"
                    textAlign="center"
                    alignSelf="center"
                >
                    By confirming, I authorize Fruitful to initiate the transfer
                    as stated above.
                </FootnoteRegular>
                <Button
                    disabled={!isValid}
                    text="Confirm transfer"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={async () => {
                        setIsSubmitting(true);
                        try {
                            const response = await submitIctDepositRequest({
                                body: ictDepositRequest,
                            });
                            track(AnalyticsUserEvent.IM_DEPOSIT_INITIATED);
                            // TODO Come up with better error states
                            //  https://linear.app/fruitful/issue/ENG-5813/more-detailed-error-states-for-ict-deposit

                            const errorStates = [
                                'WORKFLOW_ERROR',
                                'REJECTED',
                                'CANCELED',
                            ];
                            if (errorStates.includes(response.state)) {
                                captureWarning(
                                    `ICT deposit error: ${response.state} ${response.stateMessage}`,
                                );
                                track(AnalyticsUserEvent.IM_DEPOSIT_FAILED);
                                toast.show(
                                    'Your transfer could not be processed',
                                );
                            } else {
                                const apexAccountId =
                                    apexDepositRequest?.toAccount
                                        ?.apexAccountId;

                                if (apexAccountId != null) {
                                    await queryClient.refetchQueries({
                                        queryKey: [
                                            `/api/investments/${apexAccountId}/transactions`,
                                        ],
                                    });
                                    /* Invalidate from useGetApexAccountDetails (expanded=false) */
                                    void queryClient.invalidateQueries({
                                        queryKey: [
                                            `/investments/account/${apexAccountId}/details?expanded=false`,
                                        ],
                                    });
                                    /* Invalidate from useGetApexAccountDetails (expanded=true) */
                                    void queryClient.invalidateQueries({
                                        queryKey: [
                                            `/investments/account/${apexAccountId}/details?expanded=true`,
                                        ],
                                    });
                                    /* Invalidate from useGetApexPortfolio */
                                    void queryClient.invalidateQueries({
                                        queryKey: [
                                            `/api/investments/${apexAccountId}/portfolio`,
                                        ],
                                    });
                                    void queryClient.invalidateQueries({
                                        queryKey: [
                                            `/api/investments/${apexAccountId}/performance`,
                                        ],
                                    });
                                }

                                /* Invalidate from useGetApexAccounts */
                                void queryClient.invalidateQueries({
                                    queryKey: ['/investments/accounts'],
                                });

                                router.navigate(
                                    '/investments/transfer/deposit-submitted',
                                );
                            }
                        } catch (error) {
                            captureException(error);
                            track(AnalyticsUserEvent.IM_DEPOSIT_FAILED);
                            toast.show('Something went wrong');
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                />
            </YStack>
        </ScreenContainer>
    );
};
